/**
 * Static data for the three-agent collision scenario. The setting is the demo app's
 * "Season 2" spec (a generic community league ladder: players, matches, results, rankings).
 * Work package ids WP-21 to WP-23 are fictional and never clash with the sprint's WP-00 to WP-12.
 */
import type { DeclareClaimInput, RegisterSessionInput } from "../coordinator.ts";
import type { WorkPlanDraft } from "../plan.ts";
import { makeScope } from "../primitives.ts";
import type { StackManifest } from "../stack.ts";

/** Fake but well-formed commit SHAs used throughout the scenario. */
export const SHA = {
  base: "0b".repeat(20),
  alpha1: "a1".repeat(20),
  bravo1: "b1".repeat(20),
  bravo2: "b2".repeat(20),
  charlie1: "c1".repeat(20),
  mergeAlpha: "e1".repeat(20),
  mergeBravo: "e2".repeat(20),
} as const;

/** The three agents, as they register. */
export const AGENTS = {
  alpha: {
    agentName: "alpha",
    wpId: "WP-21",
    forkRepo: "botallion-demo/ladder-wp-21",
    forkRemote: "https://artifacts.example.dev/botallion-demo/ladder-wp-21.git",
  },
  bravo: {
    agentName: "bravo",
    wpId: "WP-22",
    forkRepo: "botallion-demo/ladder-wp-22",
    forkRemote: "https://artifacts.example.dev/botallion-demo/ladder-wp-22.git",
  },
  charlie: {
    agentName: "charlie",
    wpId: "WP-23",
    forkRepo: "botallion-demo/ladder-wp-23",
    forkRemote: "https://artifacts.example.dev/botallion-demo/ladder-wp-23.git",
  },
} as const satisfies Record<string, RegisterSessionInput>;

/** Agent key in {@link AGENTS}. */
export type AgentKey = keyof typeof AGENTS;

/** The plan the planner produced for the Season 2 spec. */
export const SEASON_2_PLAN: WorkPlanDraft = {
  specId: "season-2",
  title: "Season 2",
  packages: [
    {
      id: "WP-21",
      title: "Seasons and divisions",
      specExcerpt: "Matches belong to a season. Each season splits players into divisions; the ladder is shown per season and division.",
      scope: makeScope({
        paths: ["demo/api/migrations/**", "demo/api/src/routes/seasons.ts", "demo/api/src/routes/ladder.ts"],
        contracts: ["schema:seasons", "schema:matches", "api:GET /ladder"],
        migration: true,
      }),
      acceptanceCriteria: [
        { id: "AC-1", text: "A migration adds seasons and divisions tables and matches.season_id.", check: "Migration file applies cleanly to an empty D1 and to the Season 1 schema." },
        { id: "AC-2", text: "GET /ladder?season=<id> returns standings grouped by division.", check: "Vitest: ladder.season.test.ts" },
      ],
      dependsOn: [],
    },
    {
      id: "WP-22",
      title: "Match disputes",
      specExcerpt: "A player can dispute a reported result. Disputed results are excluded from the ladder until resolved.",
      scope: makeScope({
        paths: ["demo/api/migrations/**", "demo/api/src/routes/matches.ts", "demo/api/src/lib/validation.ts"],
        symbols: ["demo/api/src/lib/ladder.ts#applyResult"],
        contracts: ["schema:matches", "api:POST /matches/:id/dispute", "module:demo/api/src/lib/validation.ts"],
        migration: true,
      }),
      acceptanceCriteria: [
        { id: "AC-1", text: "POST /matches/:id/dispute returns 201 and stores the dispute.", check: "Vitest: matches.dispute.test.ts" },
        { id: "AC-2", text: "Disputed results do not count towards the ladder.", check: "Vitest: ladder.disputes.test.ts" },
      ],
      dependsOn: [],
    },
    {
      id: "WP-23",
      title: "Elo ratings",
      specExcerpt: "Replace points with Elo ratings. The ladder shows each player's rating; jobs recalculates ratings every 15 minutes.",
      scope: makeScope({
        paths: ["demo/jobs/src/**", "demo/api/src/lib/ladder.ts", "demo/api/src/routes/ladder.ts"],
        symbols: ["demo/api/src/lib/ladder.ts#applyResult"],
        contracts: ["api:GET /ladder"],
      }),
      acceptanceCriteria: [
        { id: "AC-1", text: "applyResult updates both players' Elo with K=32.", check: "Vitest: elo.test.ts" },
        { id: "AC-2", text: "GET /ladder rows include a rating field.", check: "Vitest: ladder.rating.test.ts" },
      ],
      dependsOn: [],
    },
  ],
  questions: [
    { id: "Q-1", text: "Do disputed results freeze both players' ratings, or only the disputing player's?", blocking: true, answer: null },
    { id: "Q-2", text: "Should divisions be rebalanced mid-season?", blocking: false, answer: null },
  ],
  sequencingNotes: [
    "WP-21 and WP-22 both migrate: the migration lane sequences them, WP-21 first because seasons are a prerequisite for disputes per season.",
    "WP-23 and WP-21 both change GET /ladder: left for runtime negotiation.",
    "WP-22 and WP-23 both touch applyResult: left for runtime negotiation.",
  ],
};

/** Every claim the agents declare, in declaration order. */
export const CLAIMS: { key: string; agent: AgentKey; input: Omit<DeclareClaimInput, "sessionId"> }[] = [
  {
    key: "alpha.schema",
    agent: "alpha",
    input: {
      scope: makeScope({ paths: ["demo/api/migrations/**"], contracts: ["schema:seasons", "schema:matches"], migration: true }),
      rationale: "Add seasons and divisions tables and a season_id column on matches.",
      mode: "exclusive",
    },
  },
  {
    key: "alpha.ladder",
    agent: "alpha",
    input: {
      scope: makeScope({ paths: ["demo/api/src/routes/seasons.ts", "demo/api/src/routes/ladder.ts"], contracts: ["api:GET /ladder"] }),
      rationale: "The ladder endpoint gains a ?season= filter and groups standings by division.",
      mode: "exclusive",
    },
  },
  {
    key: "bravo.schema",
    agent: "bravo",
    input: {
      scope: makeScope({ paths: ["demo/api/migrations/**"], contracts: ["schema:matches"], migration: true }),
      rationale: "Add a disputes table and a disputed flag on matches.",
      mode: "exclusive",
    },
  },
  {
    key: "bravo.disputes",
    agent: "bravo",
    input: {
      scope: makeScope({
        paths: ["demo/api/src/routes/matches.ts", "demo/api/src/lib/validation.ts"],
        symbols: ["demo/api/src/lib/ladder.ts#applyResult"],
        contracts: ["api:POST /matches/:id/dispute"],
      }),
      rationale: "Dispute endpoint and shared result validation; applyResult must skip disputed results.",
      mode: "exclusive",
    },
  },
  {
    key: "charlie.elo",
    agent: "charlie",
    input: {
      scope: makeScope({ paths: ["demo/jobs/src/**", "demo/api/src/lib/ladder.ts"], symbols: ["demo/api/src/lib/ladder.ts#applyResult"] }),
      rationale: "Replace points with Elo in the ladder calculation and the recalculation job.",
      mode: "shared",
    },
  },
  {
    key: "charlie.ladder",
    agent: "charlie",
    input: {
      scope: makeScope({ paths: ["demo/api/src/routes/ladder.ts"], contracts: ["api:GET /ladder"] }),
      rationale: "Ladder rows gain a rating field.",
      mode: "shared",
    },
  },
];

/** The demo app's stack. */
export const LADDER_STACK: StackManifest = {
  name: "ladder",
  workers: [
    {
      name: "api",
      entry: "demo/api/src/index.ts",
      routes: ["ladder-api.example.dev/*"],
      compatibilityDate: "2026-09-01",
      secrets: [
        {
          name: "RESULTS_SIGNING_KEY",
          source: { type: "cf-secrets-store", storeId: "store-ladder", secretName: "ladder-results-signing-key", previewSecretName: "ladder-results-signing-key-preview" },
        },
      ],
    },
    { name: "jobs", entry: "demo/jobs/src/index.ts", routes: [], crons: ["*/15 * * * *"], compatibilityDate: "2026-09-01" },
    { name: "web", entry: "demo/web/src/index.ts", routes: ["ladder.example.dev/*"], compatibilityDate: "2026-09-01" },
  ],
  d1: [{ binding: "DB", name: "ladder", migrationsDir: "demo/api/migrations", workers: ["api", "jobs"] }],
  r2: [{ binding: "AVATARS", name: "avatars", workers: ["api"] }],
  kv: [],
  services: [{ worker: "web", binding: "API_SERVICE", service: "api" }],
  deployOrder: ["api", "jobs", "web"],
  previewNaming: "{stack}-{branch}-{resource}",
  productionNaming: "{stack}-{resource}",
};
